import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[var(--main-bg)]" data-testid="terms-page">
      <div className="max-w-3xl mx-auto p-6 sm:p-12">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--obsidian)] mb-8 transition-colors"
          data-testid="terms-back-btn"
        >
          <ArrowLeft size={20} />
          <span className="text-sm font-medium">Back</span>
        </button>

        <h1 className="text-3xl sm:text-5xl font-black tracking-tight mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Terms of Service
        </h1>
        <p className="text-sm text-[var(--text-secondary)] font-mono uppercase tracking-wider mb-10">
          Last updated: April 5, 2026
        </p>

        <div className="space-y-8 text-[var(--text-primary)]">
          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>1. Acceptance of Terms</h2>
            <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
              By accessing or using VAULT ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service. We reserve the right to update these Terms at any time, and your continued use of the Service constitutes acceptance of any changes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>2. Description of Service</h2>
            <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
              VAULT is an idea management platform that allows users to capture, organize, categorize, enhance, and export their ideas. The Service includes AI-powered idea enhancement, team collaboration features, and subscription-based premium features.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>3. User Accounts</h2>
            <div className="text-sm leading-relaxed text-[var(--text-secondary)] space-y-3">
              <p>You must provide accurate, complete information when creating an account. You are responsible for maintaining the security of your account credentials, including any two-factor authentication methods.</p>
              <p>You are solely responsible for all activity that occurs under your account. Notify us immediately if you suspect unauthorized access.</p>
              <p>You must be at least 13 years of age to use the Service. If you are under 18, you must have parental or guardian consent.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>4. Intellectual Property — Your Content</h2>
            <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-5 mb-3">
              <p className="text-sm leading-relaxed font-medium">
                You retain full ownership of all ideas, content, and intellectual property you create or store within VAULT. We do not claim any ownership rights over your content.
              </p>
            </div>
            <div className="text-sm leading-relaxed text-[var(--text-secondary)] space-y-3">
              <p>By using the Service, you grant VAULT a limited, non-exclusive license to store, process, and display your content solely for the purpose of providing the Service to you.</p>
              <p>We will never sell, share, or publicly disclose your ideas or content without your explicit consent.</p>
              <p>When you use the AI Enhancement feature, your content is sent to a third-party AI provider (OpenAI) for processing. We do not store AI-processed content beyond what is saved back to your account at your direction.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>5. Intellectual Property — Our Service</h2>
            <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
              The VAULT name, logo, design, code, and all associated intellectual property are owned by VAULT and protected by copyright, trademark, and other applicable laws. You may not copy, modify, distribute, or reverse-engineer any part of the Service without our express written permission.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>6. Subscription & Payments</h2>
            <div className="text-sm leading-relaxed text-[var(--text-secondary)] space-y-3">
              <p>VAULT offers free and paid subscription plans. Paid plans are billed through Stripe, a third-party payment processor. By subscribing to a paid plan, you agree to Stripe's Terms of Service.</p>
              <p><strong>Recurring billing:</strong> Paid subscriptions automatically renew at the end of each billing period unless cancelled.</p>
              <p><strong>Cancellation:</strong> You may cancel your subscription at any time from your account settings. Cancellation takes effect at the end of the current billing period. No partial refunds are provided.</p>
              <p><strong>Founding Member:</strong> The Founding Member plan is a one-time payment for lifetime access to Pro features. This is non-refundable after 14 days.</p>
              <p><strong>Price changes:</strong> We reserve the right to change pricing with 30 days advance notice. Existing subscribers will be grandfathered at their current rate for at least 12 months.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>7. Acceptable Use</h2>
            <div className="text-sm leading-relaxed text-[var(--text-secondary)] space-y-3">
              <p>You agree NOT to:</p>
              <ul className="list-disc list-inside space-y-1 pl-2">
                <li>Use the Service for any illegal or unauthorized purpose</li>
                <li>Attempt to gain unauthorized access to other accounts or systems</li>
                <li>Upload malicious code, viruses, or harmful content</li>
                <li>Use the Service to spam, harass, or abuse others</li>
                <li>Resell, sublicense, or redistribute the Service without permission</li>
                <li>Use automated tools to scrape or extract data from the Service</li>
                <li>Interfere with the Service's infrastructure or other users' access</li>
              </ul>
              <p>Violation of these terms may result in immediate account suspension or termination.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>8. Team Collaboration</h2>
            <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
              When you share ideas with a team, other team members may view, copy, or build upon those ideas according to the permissions you set. You are responsible for managing access to your shared content. VAULT is not responsible for how team members use shared ideas.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>9. Data Export & Portability</h2>
            <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
              You may export your ideas at any time in Markdown or JSON format. Upon account deletion, we will permanently remove all your data within 30 days, except where retention is required by law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>10. Service Availability</h2>
            <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
              We strive to maintain high availability but do not guarantee uninterrupted access. The Service may be temporarily unavailable for maintenance, updates, or circumstances beyond our control. We are not liable for any losses caused by service interruptions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>11. Limitation of Liability</h2>
            <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, VAULT AND ITS OPERATORS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES, ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE SERVICE IN THE 12 MONTHS PRECEDING THE CLAIM.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>12. Disclaimer of Warranties</h2>
            <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. AI-generated enhancements are provided for informational purposes and should not be relied upon as professional advice.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>13. Termination</h2>
            <div className="text-sm leading-relaxed text-[var(--text-secondary)] space-y-3">
              <p>We may suspend or terminate your account at any time for violation of these Terms, with or without notice.</p>
              <p>You may delete your account at any time. Upon termination, your right to use the Service ceases immediately. You may export your data before deletion.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>14. Governing Law</h2>
            <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
              These Terms shall be governed by and construed in accordance with the laws of the United States. Any disputes shall be resolved through binding arbitration, except where prohibited by law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>15. Contact</h2>
            <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
              If you have questions about these Terms, contact us through the Support page within the app or email us at the address provided in your account settings.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
